import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import InfoTooltip from "./InfoTooltip";
import StandardsHeatmap from "./StandardsHeatmap";
import MultiSelectDropdown from "./filters/MultiSelectDropdown";
import { useAccounts, accountLabel } from "@/hooks/useAccounts";
import { useAuthStore } from "@/store/useAuthStore";
import Dropdown from "@/components/Dropdown";
import { Size } from "@ever-tutor/et-ui";
import StandardDetailDrawer, {
  type DrawerTarget,
} from "./StandardDetailDrawer";
import ExitTicketDetailModal from "./modals/ExitTicketDetailModal";
import Item from "./scrollable/Item";
import { STANDARDS_FRAMEWORKS } from "../constants/standardsFrameworkConfig";
import {
  MASTERY_CATEGORIES,
  bucketLabel,
  bucketOf,
  flattenCodes,
  mergeDomainsByCode,
} from "../service/mastery";
import { MasteryLevel } from "../types/enums";
import { GRADE_ORDER } from "../types/standards";
import type {
  StudentMastery,
  DomainNode,
  Grade,
  GradeMode,
  StandardsResponse,
} from "../types/standards";
import {
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";

const comparePillKeys = (a: string, b: string): number => {
  const ai = GRADE_ORDER.indexOf(a as Grade);
  const bi = GRADE_ORDER.indexOf(b as Grade);
  if (ai !== -1 && bi !== -1) return ai - bi;
  if (ai !== -1) return -1;
  if (bi !== -1) return 1;
  return a.localeCompare(b);
};
import api from "@/utils/axios-utils";
import {
  demoStandardsFramework,
  isDemoAccount,
  loadDemoStandards,
} from "../service/demoAnalytics";

type FilterKey = "teachers" | "competency" | "data";

export type StandardsDataState = {
  students: StudentMastery[];
  loading: boolean;
  error: boolean;
  retry: () => void;
};

const StandardsHeatmapViewContent = ({
  onStateChange,
  headerActions,
}: {
  onStateChange?: (state: StandardsDataState) => void;
  headerActions?: ReactNode;
}) => {
  const {
    accounts,
    isLoading: isLoadingAccounts,
    error: accountsError,
  } = useAccounts();
  // Fixed for the page's life: it hangs off the signed-in account, and signing
  // in as someone else remounts the dashboard.
  const framework = useMemo(
    () => STANDARDS_FRAMEWORKS[demoStandardsFramework()],
    [],
  );
  const actingTeacherId = useAuthStore((s) => s.actingTeacherId);
  const [teacherSelection, setSelectedTeachers] = useState<Set<number> | null>(
    null,
  );
  const actingTeacherUserId = accounts.find(
    (a) => a.teacher_id === actingTeacherId,
  )?.user_id;
  // A switched teacher missing from accounts is either still loading (hold) or
  // unresolvable (failed lookup, or not returned): then fall back to all
  // students rather than an empty view that reads as "No students yet".
  const isActingTeacherPending =
    actingTeacherId !== null &&
    actingTeacherUserId === undefined &&
    isLoadingAccounts &&
    accountsError == null;
  const isActingTeacherUnresolved =
    actingTeacherId !== null &&
    actingTeacherUserId === undefined &&
    !isActingTeacherPending;
  const selectedTeachers = useMemo(
    () =>
      teacherSelection ??
      new Set(actingTeacherUserId === undefined ? [] : [actingTeacherUserId]),
    [teacherSelection, actingTeacherUserId],
  );
  const [gradeMode, setGradeMode] = useState<GradeMode>("instruction");
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);
  const [selectedGrades, setSelectedGrades] = useState<Set<string> | null>(
    null,
  );
  const [showWithoutDirectEvidence, setShowWithoutDirectEvidence] =
    useState(false);
  const [showAverage, setShowAverage] = useState(false);
  const { showNotification } = useNotificationStore();
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget | null>(null);
  const [exitTicket, setExitTicket] = useState<{
    attemptId: number;
    studentName: string;
  } | null>(null);

  const [allStudents, setAllStudents] = useState<StudentMastery[]>([]);
  const [loadingStandards, setLoadingStandards] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchStandards = async () => {
      setLoadingStandards(true);
      setLoadError(false);
      try {
        // Demo accounts read a frozen capture instead — see service/demoAnalytics.
        // The cache-backed endpoint takes no point-in-time cut any more, so the
        // capture is served exactly as it was recorded.
        const payload: StandardsResponse = isDemoAccount("standards")
          ? ((await loadDemoStandards()) as StandardsResponse)
          : (await api.get<StandardsResponse>("/analytics/standards")).data;
        const mapped: StudentMastery[] = payload.students.map((s) => ({
          user_id: s.user_id,
          name: s.name,
          teacher_user_ids: s.teacher_user_ids,
          grade: s.grade ?? undefined,
          cells: Object.fromEntries(
            Object.entries(s.cells).map(([code, cell]) => [
              code,
              {
                mastery: cell.mastery,
                evidence: cell.evidence,
                started: cell.started,
                lessons: cell.lessons ?? [],
              },
            ]),
          ),
        }));
        if (cancelled) return;
        setAllStudents(mapped);
        setComputedAt(payload.computed_at ?? null);
      } catch (err) {
        if (!cancelled) {
          setLoadError(true);
          console.error("[standards] fetch failed", err);
          showNotification(
            "Failed to load standards data.",
            NotificationType.WARNING,
          );
        }
      } finally {
        if (!cancelled) setLoadingStandards(false);
      }
    };
    fetchStandards();
    return () => {
      cancelled = true;
    };
  }, [showNotification, retry]);

  const retryStandards = useCallback(() => setRetry((value) => value + 1), []);
  useEffect(() => {
    onStateChange?.({
      students: allStudents,
      loading: loadingStandards,
      error: loadError,
      retry: retryStandards,
    });
  }, [allStudents, loadingStandards, loadError, retryStandards, onStateChange]);

  const toggleFilter = (key: FilterKey) =>
    setOpenFilter((prev) => (prev === key ? null : key));

  const changeGradeMode = (mode: GradeMode) => {
    setGradeMode(mode);
    setSelectedGrades(null);
    setOpenFilter(null);
  };

  const filterBarRef = useRef<HTMLDivElement>(null);
  const dataDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!openFilter) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !filterBarRef.current?.contains(target) &&
        !dataDropdownRef.current?.contains(target)
      )
        setOpenFilter(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openFilter]);

  const masteryFreshnessLabel = useMemo(() => {
    if (computedAt === null) return "Mastery not yet calculated";
    const d = new Date(computedAt);
    return `Mastery as of ${d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}`;
  }, [computedAt]);

  const teacherOptions = useMemo(
    () =>
      accounts
        .map((a) => ({ value: a.user_id, label: accountLabel(a) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts],
  );
  // A failed refresh keeps the last data; a failed first load has none, so the
  // alert stands alone instead of beside an empty state.
  const isFirstLoadFailed = loadError && allStudents.length === 0;
  const students = useMemo<StudentMastery[]>(
    () =>
      allStudents.filter(
        (s) =>
          selectedTeachers.size === 0 ||
          s.teacher_user_ids?.some((id) => selectedTeachers.has(id)),
      ),
    [allStudents, selectedTeachers],
  );

  // Columns and grade options require direct evidence from any student;
  // each displayed cell separately requires direct evidence and started mastery.
  const directEvidenceCodes = useMemo(() => {
    const set = new Set<string>();
    students.forEach((s) => {
      for (const code in s.cells) if (s.cells[code].evidence > 0) set.add(code);
    });
    return set;
  }, [students]);

  const gradeOptions = useMemo(() => {
    const evidenceBuckets = new Set<string>();
    framework.map.forEach((d) => {
      const bucket = bucketOf(d);
      if (bucket == null) return;
      const hasEvidence = d.clusters.some((c) =>
        c.standards.some((s) => directEvidenceCodes.has(s.id)),
      );
      if (hasEvidence) evidenceBuckets.add(bucket);
    });
    if (gradeMode === "enrolled") {
      const buckets = new Set<string>();
      students.forEach((s) => {
        if (s.grade == null || s.grade === "HS") return;
        if (evidenceBuckets.has(s.grade)) buckets.add(s.grade);
      });
      if (students.some((s) => s.grade === "HS")) {
        framework.map
          .filter((d) => d.category != null)
          .forEach((d) => {
            const bucket = bucketOf(d)!;
            if (evidenceBuckets.has(bucket)) buckets.add(bucket);
          });
      }
      return [...buckets].sort(comparePillKeys);
    }
    return [...evidenceBuckets].sort(comparePillKeys);
  }, [framework, gradeMode, students, directEvidenceCodes]);

  useEffect(() => {
    setSelectedGrades((prev) => {
      if (prev === null) return new Set(gradeOptions);
      const next = new Set([...prev].filter((g) => gradeOptions.includes(g)));
      return next.size > 0 ? next : new Set(gradeOptions);
    });
  }, [gradeOptions]);

  const activeGrades = useMemo(
    () => selectedGrades ?? new Set(gradeOptions),
    [selectedGrades, gradeOptions],
  );

  const toggleGrade = (g: string) => {
    setSelectedGrades((prev) => {
      const next = new Set(prev ?? gradeOptions);
      next.has(g) ? next.delete(g) : next.add(g);
      if (next.size === 0) next.add(g); // keep at least one selected
      return next;
    });
  };

  // Grade/category selection filters the raw, per-grade framework map at the
  // domain level (unchanged from before merging existed) — merging same-code
  // domains happens after, over whichever domains survive the filter.
  const scopedMap = useMemo<DomainNode[]>(() => {
    return framework.map.filter((d) => {
      const bucket = bucketOf(d);
      return bucket != null && activeGrades.has(bucket);
    });
  }, [framework, activeGrades]);

  const mergedMap = useMemo<DomainNode[]>(
    () => mergeDomainsByCode(scopedMap, framework.domainOrder),
    [framework, scopedMap],
  );

  const visibleMap = useMemo<DomainNode[]>(() => {
    if (showWithoutDirectEvidence) return mergedMap;
    return mergedMap
      .map((d) => ({
        ...d,
        clusters: d.clusters
          .map((c) => ({
            ...c,
            standards: c.standards.filter((s) => directEvidenceCodes.has(s.id)),
          }))
          .filter((c) => c.standards.length > 0),
      }))
      .filter((d) => d.clusters.length > 0);
  }, [mergedMap, directEvidenceCodes, showWithoutDirectEvidence]);

  const allCodes = useMemo(() => flattenCodes(visibleMap), [visibleMap]);

  // If the current filter combination (grade pills, teacher, evidence toggle)
  // leaves zero standards with evidence, fall back to the full grade-scoped
  // structure so the grid still renders — header rows and the full roster,
  // every cell just "not started" — instead of hiding it behind a message.
  const displayMap = allCodes.length > 0 ? visibleMap : mergedMap;
  const displayCodes = useMemo(() => flattenCodes(displayMap), [displayMap]);

  const toggleIn = <T,>(set: Set<T>, value: T, setter: (s: Set<T>) => void) => {
    const next = new Set(set);
    next.has(value) ? next.delete(value) : next.add(value);
    setter(next);
  };

  return (
    <div className="et-standards-view">
      <header
        className={`et-analytics-greeting${headerActions ? " et-standards-view-heading" : ""}`}
      >
        <h1>Standards</h1>
        {headerActions}
      </header>
      <div ref={filterBarRef} className="et-standards-filters">
        <span className="sr-only">Filter data by:</span>
        <div className="flex flex-row flex-wrap items-center gap-3">
          {accounts.length > 1 && (
            <div className="w-[180px]">
              <MultiSelectDropdown
                label="All teachers"
                options={teacherOptions}
                searchPlaceholder="Search for a Teacher"
                selected={selectedTeachers}
                onToggleValue={(v) =>
                  toggleIn(selectedTeachers, v, setSelectedTeachers)
                }
                onClear={(values) =>
                  setSelectedTeachers((prev) => {
                    const next = new Set(prev ?? selectedTeachers);
                    values.forEach((v) => next.delete(v));
                    return next;
                  })
                }
                onSelectAll={(values) =>
                  setSelectedTeachers(
                    (prev) =>
                      new Set([...(prev ?? selectedTeachers), ...values]),
                  )
                }
                isOpen={openFilter === "teachers"}
                onToggle={() => toggleFilter("teachers")}
              />
            </div>
          )}
          <div className="w-[220px]">
            <Dropdown
              title={
                gradeMode === "enrolled"
                  ? "Enrolled grade"
                  : "Instruction level"
              }
              isOpen={openFilter === "competency"}
              onToggle={() => toggleFilter("competency")}
              variant="analytics"
              size={Size.SM}
            >
              <div className="flex flex-col gap-1 min-w-[200px] font-mulish py-1">
                <div className="text-[0.7rem] font-extrabold uppercase tracking-[0.08em] et-analytics-placeholder text-[#B4B4B4] px-2 pb-1">
                  Type of grade
                </div>
                {(
                  [
                    ["instruction", "Instruction level"],
                    ["enrolled", "Enrolled grade"],
                  ] as const
                ).map(([value, label]) => {
                  const on = gradeMode === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => changeGradeMode(value)}
                      className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-[#F4FAF6] text-left"
                    >
                      <span
                        className="w-5 h-5 rounded-full border-2 grid place-items-center flex-none"
                        style={{ borderColor: on ? "#1C9C6D" : "#C7CDD3" }}
                      >
                        {on && (
                          <span className="w-[10px] h-[10px] rounded-full bg-[#1C9C6D]" />
                        )}
                      </span>
                      <span className="text-base text-[#252525]">{label}</span>
                    </button>
                  );
                })}
              </div>
            </Dropdown>
          </div>
          {gradeOptions.length > 0 && (
            <div className="et-standards-grades">
              <span className="font-mulish text-[#25252599] text-sm whitespace-nowrap mr-1">
                {gradeMode === "enrolled"
                  ? "Enrolled grade"
                  : "Instruction level"}{" "}
                :
              </span>
              <InfoTooltip
                label="About grade types"
                info="Instruction level is the grade level of the standards a student is working on. Enrolled grade is the grade the student is officially in. The Type of grade filter switches which one drives the columns."
              />
              {gradeOptions.map((g) => {
                const on = activeGrades.has(g);
                return (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleGrade(g)}
                    className={`font-mulish text-[0.8rem] font-extrabold rounded-full px-[14px] py-[6px] border-[1.5px] transition-colors ${
                      on
                        ? "bg-[#1C9C6D] border-[#1C9C6D] text-white"
                        : "bg-white border-[#1C9C6D]/35 text-[#007047] hover:bg-[#F0FAF4]"
                    }`}
                  >
                    {bucketLabel(g)}
                  </button>
                );
              })}
            </div>
          )}
          <p className="et-standards-freshness">{masteryFreshnessLabel}</p>
        </div>
      </div>

      <div className="et-standards-panel">
        {loadError && (
          <div role="alert" className="et-assessment-error">
            {isFirstLoadFailed
              ? "Could not load standards data."
              : "Could not refresh standards data."}{" "}
            <button
              type="button"
              onClick={() => setRetry((value) => value + 1)}
            >
              Retry
            </button>
          </div>
        )}
        <h2 className="et-standards-title">
          Standard Mastery{" "}
          <span className="font-mulish font-semibold text-[#25252599]">
            {framework.label}
          </span>
          <InfoTooltip
            label="About Standard Mastery"
            info={`This is demonstrated mastery (what students have shown), not true mastery. It comes from their lessons and exit tickets. ${MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label}: no directly evidenced mastery on this standard. Approaching: about 80% of a lesson done, but no exit-ticket problem right yet. Proficient: at least one exit-ticket problem right. Mastered: all (or 80%+) of the exit-ticket problems right.`}
          />
        </h2>

        <div className="et-standards-toolbar">
          <span className="sr-only">Demonstrated mastery :</span>
          {Object.values(MASTERY_CATEGORIES).map((cat) => (
            <div key={cat.key} className="et-mastery-legend-item">
              <Item width={14} height={14}>
                <div
                  className={`p-2 rounded-md w-full h-full ${cat.key === MasteryLevel.NOT_STARTED ? "et-standards-not-started" : ""}`}
                  style={{
                    backgroundColor:
                      cat.key === MasteryLevel.NOT_STARTED ? "#fff" : cat.dot,
                    border:
                      cat.key === MasteryLevel.NOT_STARTED
                        ? "1.5px solid #E2E5E9"
                        : "none",
                  }}
                />
              </Item>
              <span className="font-mulish text-[#252525] text-base font-semibold">
                {cat.label}
              </span>
            </div>
          ))}
          <div className="et-standards-data-controls" ref={dataDropdownRef}>
            <Dropdown
              title="Show data"
              isOpen={openFilter === "data"}
              onToggle={() => toggleFilter("data")}
              variant="analytics"
              size={Size.SM}
              menuClassName="md:!left-auto md:!right-0"
            >
              <div className="flex flex-col font-mulish min-w-[220px]">
                <label className="flex items-center gap-3 px-0.5 py-2 font-semibold text-[#252525] cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-[#1C9C6D]"
                    checked={showAverage}
                    onChange={(e) => setShowAverage(e.target.checked)}
                  />
                  <span className="text-base">Cohort average</span>
                </label>
                <label className="flex items-center gap-3 px-0.5 py-2 font-semibold text-[#252525] cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-[#1C9C6D]"
                    checked={showWithoutDirectEvidence}
                    onChange={(e) =>
                      setShowWithoutDirectEvidence(e.target.checked)
                    }
                  />
                  <span className="text-base">
                    Standards without direct evidence
                  </span>
                </label>
              </div>
            </Dropdown>
          </div>
        </div>

        {isActingTeacherUnresolved && teacherSelection === null && (
          <p role="status" className="font-mulish text-sm text-[#7D7D7D]">
            Couldn&apos;t confirm the switched teacher, so all students are
            shown.
          </p>
        )}
        {loadingStandards || isActingTeacherPending ? (
          <p className="font-mulish et-analytics-placeholder text-[#B4B4B4] text-sm py-4">
            Loading standards data...
          </p>
        ) : isFirstLoadFailed ? null : displayCodes.length > 0 ? (
          <StandardsHeatmap
            students={students}
            map={displayMap}
            framework={framework}
            gradeMode={gradeMode}
            showAverage={showAverage}
            onOpenDetail={setDrawerTarget}
          />
        ) : students.length === 0 ? (
          <p className="font-mulish et-analytics-placeholder text-[#B4B4B4] text-sm py-4">
            {selectedTeachers.size === 0
              ? "No students yet."
              : `No students taught by the selected teacher${
                  selectedTeachers.size > 1 ? "s" : ""
                }.`}
          </p>
        ) : (
          <p className="font-mulish et-analytics-placeholder text-[#B4B4B4] text-sm py-4">
            No evidence yet. Standards appear once students complete exit
            tickets with standards attached.
          </p>
        )}
      </div>

      <StandardDetailDrawer
        target={drawerTarget}
        onClose={() => setDrawerTarget(null)}
        onOpenExitTicket={setExitTicket}
        disableEscape={!!exitTicket}
        terms={framework.terms}
      />
      <ExitTicketDetailModal
        isOpen={!!exitTicket}
        onClose={() => setExitTicket(null)}
        attemptId={exitTicket?.attemptId ?? null}
        studentName={exitTicket?.studentName ?? ""}
        isDemo={isDemoAccount("assessment-attempts")}
      />
    </div>
  );
};

export default StandardsHeatmapViewContent;
